'use client'

import { Field, Input, Stack, Textarea, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddressSearch } from '@/components/atoms/Form/AddressSearch/AddressSearch'
import { ImageDropzoneForm } from '@/components/atoms/Form/ImageDropzoneForm'
import { MapProvider } from '@/components/atoms/Form/MapContext'
import { NetworkCardPreview } from '@/components/molecules/Cards/NetworkCard/NetworkCardPreview'
import { NetworkColorPickerField } from '@/components/molecules/Form/Networks/NetworkColorPickerField'
import { StepperDiptychLayout } from '@/components/templates/StepperDiptychLayout'

import { NetworkNewWizardStepNav } from './NetworkNewWizardStepNav'
import type { NetworkWizardFormValues } from './networkWizardTypes'

function NetworkNewWizardGeneralLivePreview() {
  const { control } = useFormContext<NetworkWizardFormValues>()
  const name = useWatch({ control, name: 'name' }) ?? ''
  const description = useWatch({ control, name: 'description' }) ?? ''
  const logo = useWatch({ control, name: 'logo' })
  const primaryColor = useWatch({ control, name: 'primaryColor' })

  return (
    <NetworkCardPreview
      name={name}
      description={description}
      logo={logo instanceof File ? logo : undefined}
      primaryColor={primaryColor || undefined}
    />
  )
}

function NetworkNewWizardGeneralFields() {
  const { t } = useTranslation()
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<NetworkWizardFormValues>()
  const [logoPreview, setLogoPreview] = useState<string | false>(false)
  const [, setIsImageModalOpen] = useState(false)

  useEffect(() => {
    return () => {
      if (typeof logoPreview === 'string' && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview)
      }
    }
  }, [logoPreview])

  return (
    <VStack gap={6} w="full" align="stretch">
      <Field.Root required invalid={!!errors.name}>
        <Field.Label>{t('networks.wizard.general.name.label')}</Field.Label>
        <Input
          {...register('name', { required: true })}
          placeholder={t('networks.wizard.general.name.placeholder')}
        />
        {errors.name && (
          <Field.ErrorText>
            {t('networks.wizard.general.name.required')}
          </Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root>
        <Field.Label>
          {t('networks.wizard.general.description.label')}
        </Field.Label>
        <Textarea
          {...register('description')}
          rows={4}
          placeholder={t('networks.wizard.general.description.placeholder')}
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('networks.wizard.general.logo.label')}</Field.Label>
        <ImageDropzoneForm
          onFileChange={(file, closeModal) => {
            setIsImageModalOpen(false)
            setValue('logo', file)
            setLogoPreview((prev) => {
              if (typeof prev === 'string' && prev.startsWith('blob:')) {
                URL.revokeObjectURL(prev)
              }
              return URL.createObjectURL(file)
            })
            closeModal()
          }}
          onDelete={() => {
            setLogoPreview((prev) => {
              if (typeof prev === 'string' && prev.startsWith('blob:')) {
                URL.revokeObjectURL(prev)
              }
              return false
            })
            setValue('logo', undefined)
          }}
          preview={logoPreview}
          aspectRatio={1}
          maxFiles={1}
          onImageModalOpenChange={setIsImageModalOpen}
        />
      </Field.Root>

      <Controller
        control={control}
        name="primaryColor"
        render={({ field }) => (
          <NetworkColorPickerField
            label={t('networks.wizard.general.color.label')}
            hint={t('networks.wizard.general.color.helper')}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Field.Root>
        <Field.Label>{t('networks.wizard.general.location.label')}</Field.Label>
        <Field.HelperText>
          {t('networks.wizard.general.location.helper')}
        </Field.HelperText>
        <MapProvider>
          <AddressSearch
            key="network-wizard-address"
            placeholder={t('communities.form.address.placeholder')}
            onAddressChanged={(event) => {
              const { coordinates, address } = event
              if (!coordinates?.lat || !coordinates?.lng) {
                return
              }
              setValue('address', address || '')
              setValue('latitude', `${coordinates.lat}`)
              setValue('longitude', `${coordinates.lng}`)
              setValue('explicitLocationLabel', address || '')
            }}
            defaultValue={{
              address: watch('address') || null,
              latitude: watch('latitude') || null,
              longitude: watch('longitude') || null,
            }}
            mode="light"
          />
        </MapProvider>
      </Field.Root>
    </VStack>
  )
}

export const NetworkNewWizardGeneralStep = () => {
  const { t } = useTranslation()

  return (
    <StepperDiptychLayout
      title={t('networks.wizard.steps.general.title')}
      description={t('networks.wizard.steps.general.description')}
      shellGradient
      rightContentAlignment="center"
      leftElement={
        <Stack gap={{ base: 6, md: 8 }} w="full" maxW="xl" alignSelf="stretch">
          <NetworkNewWizardGeneralFields />
          <NetworkNewWizardStepNav />
        </Stack>
      }
      rightElement={<NetworkNewWizardGeneralLivePreview />}
    />
  )
}
